<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Transport documents"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasTransportDocumentsData"
            :has-errors="hasSectionErrors"
          />
          Transport documents
        </span>
      </template>
      <div
        v-for="(doc, docIndex) in parcel.transport_documents"
        :key="docIndex"
        class="border rounded pl-3 pr-3 pt-1 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport document type"
              :rules="rowHasData(doc) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Transport document type <span class="text-danger">*</span>
                  <small class="text-muted">1205002000</small>
                </label>
                <b-form-input
                  v-model="doc.transport_document_type"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                  placeholder="e.g. N703"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport document reference number"
              :rules="rowHasData(doc) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Transport document reference number <span class="text-danger">*</span>
                  <small class="text-muted">1205001000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Reference number of the transport document'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="doc.transport_document_reference_number"
                  v-b-tooltip.hover.top="getIssueMessage(`transport_documents.${docIndex}.transport_document_reference_number`)"
                  :class="getIssueClass(`transport_documents.${docIndex}.transport_document_reference_number`)"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          />

          <b-col
            cols="12"
            md="1"
            class="text-right"
          >
            <b-button
              v-if="!disabled"
              variant="link"
              size="sm"
              class="text-danger mt-2"
              @click="removeDocument(docIndex)"
            >
              <feather-icon icon="TrashIcon" />
              Delete
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col class="text-right">
          <b-button
            v-if="!disabled"
            variant="info"
            size="sm"
            @click="addDocument"
          >
            <feather-icon icon="PlusIcon" />
            New document
          </b-button>
        </b-col>
      </b-row>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {}
  },
  computed: {
    hasTransportDocumentsData() {
      if (!this.parcel || !this.parcel.transport_documents || !Array.isArray(this.parcel.transport_documents)) {
        return false
      }
      return this.parcel.transport_documents.some(doc => doc.transport_document_type || doc.transport_document_reference_number)
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('transport_documents.'))
    },
  },
  methods: {
    addDocument() {
      if (!this.parcel.transport_documents) {
        this.$set(this.parcel, 'transport_documents', [])
      }
      this.parcel.transport_documents.push({
        transport_document_type: '',
        transport_document_reference_number: '',
      })
    },
    removeDocument(docIndex) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this transport document?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      }).then(value => {
        if (value) {
          this.parcel.transport_documents.splice(docIndex, 1)
        }
      })
    },
    rowHasData(doc) {
      return !!(doc.transport_document_type || doc.transport_document_reference_number)
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
